import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Crop } from '@next/arable-lib/crop';
import { CropDateService, ProvinceService } from '@next/arable-lib/crop-field';
import {
  CoordinateSystem,
  GeoCalculationService,
  GeoCalculationServiceInjector,
  getMapContextState,
  MapContextState,
} from '@next/arable-lib/geo';
import { AbstractDialogResultData, AbstractFormDialog, DialogService } from '@next/core-lib/dialog';
import { AgrDateValidators } from '@next/core-lib/form';
import { LocaleService } from '@next/core-lib/i18n';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
  defaultIfEmpty,
  delay,
  filter,
  finalize,
  map,
  skip,
  startWith,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { transformedCropFields } from 'src/app/shared/helpers/crop-field-helpers';
import {
  isGroundWaterPreservationWithNitrogenCuts,
  isNutrientsPollutedArea,
} from 'src/app/shared/helpers/nutrient-polluted.helper';
import {
  CropField,
  CropFieldType,
  CropVariety,
  Province,
  SiteState,
  SoilType,
} from 'src/app/shared/models';
import { CropFieldService } from 'src/app/shared/services/crop-field.service';
import { CropVarietyService } from 'src/app/shared/services/crop-variety.service';
import { DataExchangeService } from 'src/app/shared/services/data-exchange.service';
import {
  ManualSoilAnalysis,
  SoilAnalysis,
  StateEnum,
} from 'src/app/soil-analysis/models/soil-analysis.model';
import * as SoilAnalysisActions from 'src/app/soil-analysis/store/soil-analysis.actions';
import {
  selectDefaultSoilAnalyses,
  selectSoilAnalyses,
} from 'src/app/soil-analysis/store/soil-analysis.selectors';

import { LoadAvailableTgaCrops } from 'src/app/store/actions';
import { selectAvailableTgaCrops } from 'src/app/store/selectors';

import { CropFieldDialogModel, EditCropField } from './cropfield-dialog.model';
import { PrefillCropField } from '../../store/actions/crop-field.actions';
import { LoadCropVarietiesByCropIdRequest } from '../../store/actions/crop-variety.actions';
import { isPrefilling } from '../../store/selectors/crop-field.selectors';
import { getCropVarietiesByCropId } from '../../store/selectors/crop-variety.selectors';
import { getSoilTypes } from '../../store/selectors/soil-type.selectors';

@Component({
  selector: 'app-cropfield-dialog',
  templateUrl: './cropfield-dialog.component.html',
  styleUrls: ['cropfield-dialog.component.scss'],
  standalone: false,
})
export class CropFieldDialogComponent
  extends AbstractFormDialog<CropField>
  implements OnInit, OnDestroy
{
  data = inject<CropFieldDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<CropFieldDialogComponent, AbstractDialogResultData<CropField>>>(
      MatDialogRef,
    );
  private readonly fb = inject(FormBuilder);
  private readonly geoCalculation = inject<GeoCalculationService>(GeoCalculationServiceInjector);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly cropDateService = inject(CropDateService);
  private readonly actionsListener$ = inject(ActionsSubject);
  private readonly locale = inject(LocaleService);
  private readonly provinceService = inject(ProvinceService);
  private readonly dialog = inject(DialogService);
  private readonly cropFieldService = inject(CropFieldService);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly dataExchangeService = inject(DataExchangeService);

  private readonly subscription = new Subscription();
  private readonly countToSkipInitialLoad = 1;
  eProvince = Province;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  soilAnalysesControl = new FormControl();
  @ViewChild('soilAnalysesInput') soilAnalysesInput: ElementRef<HTMLInputElement>;
  soilAnalysesFormArray = this.fb.array(this.data.cropField.soilAnalysisIds);
  private readonly selectedSoilAnalysisIds$ = this.soilAnalysesFormArray.valueChanges.pipe(
    startWith(this.data.cropField.soilAnalysisIds),
  );
  soilAnalyses$ = this.store.pipe(select(selectSoilAnalyses));
  defaultSoilAnalyses$ = this.store.pipe(select(selectDefaultSoilAnalyses));

  availableSoilAnalyses$ = combineLatest([this.soilAnalyses$, this.selectedSoilAnalysisIds$]).pipe(
    map(([soilAnalyses, soilAnalysisIds]: [SoilAnalysis[], number[]]) =>
      this.sortSoilAnalyses(
        soilAnalyses.filter(({ id = -1 }) => soilAnalysisIds.indexOf(id) === -1),
      ),
    ),
  );
  availableSoilAnalyses: SoilAnalysis[] = [];

  availableDefaultSoilAnalyses$ = combineLatest([
    this.defaultSoilAnalyses$,
    this.selectedSoilAnalysisIds$,
  ]).pipe(
    map(([soilAnalyses, soilAnalysisIds]: [SoilAnalysis[], number[]]) =>
      soilAnalyses.filter(
        ({ id = -1, state }) => state === StateEnum.Active && soilAnalysisIds.indexOf(id) === -1,
      ),
    ),
  );
  availableDefaultSoilAnalyses: SoilAnalysis[] = [];

  selectedSoilAnalyses$ = combineLatest([
    this.soilAnalyses$,
    this.defaultSoilAnalyses$,
    this.selectedSoilAnalysisIds$,
  ]).pipe(
    map(
      ([soilAnalyses, defaultSoilAnalyses, soilAnalysisIds]: [
        SoilAnalysis[],
        SoilAnalysis[],
        number[],
      ]) =>
        soilAnalysisIds.map((soilAnalysisId) =>
          soilAnalyses.concat(defaultSoilAnalyses).find(({ id }) => id === soilAnalysisId),
        ),
    ),
  );
  filteredSoilAnalyses$: Observable<SoilAnalysis[]>;
  filteredDefaultSoilAnalyses$: Observable<SoilAnalysis[]>;

  availableTgaCrops$ = this.store.pipe(select(selectAvailableTgaCrops));
  availableTgaCrops: number[] = [];
  concepts$ = this.dataExchangeService.getConcepts();
  buyerChainPartners$ = this.dataExchangeService.getBuyerChainPartners();

  formGroup = this.fb.group({
    name: [
      !this.data.isSubsequentCultivation
        ? this.data.cropField.name
        : `${this.data.cropField.name} (${this.locale.translate('subsequentCultivation')})`,
      [Validators.required, Validators.minLength(1), Validators.maxLength(100)],
    ],
    areaInHectares: [
      this.data.cropField.areaInHectares,
      [Validators.required, Validators.min(0.001), Validators.max(100000)],
    ],
    isAreaCalculated: [this.data.cropField.isAreaCalculated],
    isSkal: [this.data.cropField.isSkal],
    isPlanetProof: [this.data.cropField.isPlanetProof],
    isGroundWaterPreservation: [this.data.cropField.isGroundWaterPreservation],
    isSandySoil: [this.data.cropField.isSandySoil],
    isRentedField: [this.data.cropField.isRentedField],
    isNutrientsPollutedArea: [this.data.cropField.isNutrientsPollutedArea],
    isGroundWaterPreservationWithNitrogenCuts: [
      this.data.cropField.isGroundWaterPreservationWithNitrogenCuts,
    ],
    cropId: [this.data.cropField.cropId, [Validators.required]],
    conceptId: [this.data.cropField.conceptId ?? null],
    chainPartnerId: [this.data.cropField.chainPartnerId ?? null],
    soilTypeId: [this.data.cropField.soilTypeId, [Validators.required]],
    cropVarietyId: [this.data.cropField.cropVarietyId],
    outsideDutchBorders: [this.data.cropField.outsideDutchBorders],
    province: [
      this.data.cropField.province,
      this.getProvinceValidators(this.data.cropField.outsideDutchBorders),
    ],
    machiningDepthInCentimeters: [
      this.data.cropField.machiningDepthInCentimeters,
      [Validators.required, Validators.min(0), Validators.max(30)],
    ],
    startDate: [
      this.data.cropField.startDate
        ? this.data.isSubsequentCultivation
          ? this.addDaysToDate(new Date(this.data.cropField.startDate), 1)
          : new Date(this.data.cropField.startDate)
        : null,
      this.getStartDateValidators(
        this.data.cropField.endDate ? new Date(this.data.cropField.endDate) : null,
      ),
    ],
    endDate: [
      this.data.cropField.endDate ? new Date(this.data.cropField.endDate) : null,
      this.getEndDateValidators(
        this.data.cropField.startDate ? new Date(this.data.cropField.startDate) : null,
      ),
    ],
    soilAnalysisIds: this.soilAnalysesFormArray,
  });

  isNutrientsPollutedAreaFromLayer$: Subject<boolean> = new Subject();
  soilTypes$ = this.store.pipe(select(getSoilTypes));
  isPrefilling$ = this.store.pipe(select(isPrefilling), defaultIfEmpty(true));
  isPrefillingProvince = false;
  cropVarietiesByCropIds$ = this.store.pipe(select(getCropVarietiesByCropId));
  cropVarieties: CropVariety[] = [];
  currentCropVariety?: CropVariety;
  isLoadingBemCropVarieties = false;
  filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) =>
      this.removeDuplicateCrops([
        ...this.filter(value, [...this.data.mostUsedCrops]),
        ...this.filter(value, [...this.data.crops]),
      ]),
    ),
  );
  filteredCropVarieties$: Observable<CropVariety[]>;
  nutrientsPollutedValues$: Observable<{
    province: Province;
    isGroundWaterPreservation: boolean;
    soilTypeId: number;
    isNutrientsPollutedAreaFromLayer: boolean;
  }>;

  currentCropId: number;
  shownTgaWarning: boolean;

  ngOnInit() {
    this.subscription.add(this.setCropVarietiesSubscription(this.data.cropField.cropId));

    // Prevents ExpressionChangedAfterItHasBeenCheckedError when selected field doesn't match filter
    setTimeout(() => this.loadCropVarieties(this.data.cropField.cropId), 0);

    this.currentCropId = this.data.cropField.cropId;
    this.shownTgaWarning = false;

    if (!this.data.cropField.name) {
      this.formGroup.markAsDirty();
    }
    if (this.isParticipatingToBem()) {
      this.formGroup.get('cropId').disable();
      this.formGroup.get('cropVarietyId').disable();

      if (this.canChangeBemCropVariety()) {
        this.loadAvailableBemCropVarieties();
      }
    }
    this.setProvinceField(this.data.cropField.outsideDutchBorders);

    this.subscription.add(
      this.actionsListener$
        .pipe(ofType(SoilAnalysisActions.soilAnalysisUpserted))
        .subscribe((upserted) => {
          this.soilAnalysesFormArray.push(new FormControl(upserted.id));
        }),
    );

    this.subscription.add(
      this.formGroup.get('isAreaCalculated').valueChanges.subscribe((isAreaCalculated) => {
        if (isAreaCalculated) {
          this.setAreaInHectares();
        }
      }),
    );
    this.subscription.add(
      this.formGroup.get('areaInHectares').valueChanges.subscribe((_) => {
        this.formGroup.get('isAreaCalculated').setValue(false);
      }),
    );
    this.subscription.add(
      this.formGroup.get('cropId').valueChanges.subscribe((cropId: number) => {
        if (cropId) {
          const crop = this.data.crops.find((c) => c.id === cropId);
          if (crop) {
            if (this.data.isSubsequentCultivation) {
              this.setCalculatedEndDate(crop);
            } else {
              this.setDefaultStartAndEndDate(crop);
            }
            this.loadCropVarieties(cropId);
            this.formGroup.get('cropVarietyId').setValue(null);

            this.setCropVarietiesSubscription(cropId);

            if (
              this.data?.cropField?.cropFieldType === CropFieldType.Advice &&
              this.availableTgaCrops?.findIndex((x) => x === cropId) === -1 &&
              this.availableTgaCrops?.findIndex((x) => x === this.currentCropId) > -1 &&
              !this.shownTgaWarning
            ) {
              this.shownTgaWarning = true;
              this.dialog.notify({
                title: this.locale.translate('tgaCropChangeWarningTitle'),
                text: this.locale.translate('tgaCropChangeWarningText'),
                cancelButtonText: this.locale.translate('close'),
                pushHistoryState: false,
              });
            } else if (
              this.availableTgaCrops?.findIndex((x) => x === cropId) > -1 &&
              this.shownTgaWarning
            ) {
              this.shownTgaWarning = false;
            }
          } else {
            this.formGroup.get('cropId').setValue(null, { emitModelToViewChange: false });
          }
        }
      }),
    );

    this.subscription.add(
      this.formGroup
        .get('cropVarietyId')
        .valueChanges.subscribe((cropVarietyId: number | string) => {
          if (typeof cropVarietyId === 'string') {
            this.formGroup.get('cropVarietyId').setValue(null, { emitModelToViewChange: false });
          }
        }),
    );

    this.subscription.add(
      this.formGroup.get('startDate').valueChanges.subscribe((value: Date) => {
        this.formGroup.get('endDate').setValidators(this.getEndDateValidators(value));
        this.formGroup.get('endDate').updateValueAndValidity({ emitEvent: false });
      }),
    );
    this.subscription.add(
      this.formGroup.get('endDate').valueChanges.subscribe((value: Date) => {
        this.formGroup.get('startDate').setValidators(this.getStartDateValidators(value));
        this.formGroup.get('startDate').updateValueAndValidity({ emitEvent: false });
      }),
    );
    this.subscription.add(
      this.formGroup.get('outsideDutchBorders').valueChanges.subscribe((outsideDutchBorders) => {
        this.formGroup
          .get('province')
          .setValidators(this.getProvinceValidators(outsideDutchBorders));
        this.setProvinceField(outsideDutchBorders);
      }),
    );

    this.isPrefilling$
      .pipe(
        withLatestFrom(this.store.pipe(select(getMapContextState)), this.soilTypes$),
        delay(300),
        take(1),
      )
      .subscribe(([prefilling, mapContext, _soilTypes]: [boolean, MapContextState, SoilType[]]) => {
        if (!prefilling) {
          return;
        } else if (mapContext?.geoserverHasErrors === true) {
          this.store.dispatch(new PrefillCropField(false));
          return;
        }

        this.loadLayerData();
        this.loadProvince();
      });

    this.filteredSoilAnalyses$ = this.soilAnalysesControl.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterSoilAnalyses(value, this.availableSoilAnalyses)),
    );

    this.filteredDefaultSoilAnalyses$ = this.soilAnalysesControl.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterSoilAnalyses(value, this.availableDefaultSoilAnalyses)),
    );

    this.availableSoilAnalyses$.subscribe(
      (availableSoilAnalyses) => (this.availableSoilAnalyses = availableSoilAnalyses),
    );

    this.availableDefaultSoilAnalyses$.subscribe(
      (availableDefaultSoilAnalyses) =>
        (this.availableDefaultSoilAnalyses = availableDefaultSoilAnalyses),
    );

    this.availableTgaCrops$.subscribe(
      (availableTgaCrops) => (this.availableTgaCrops = availableTgaCrops),
    );

    this.setupUpdateNutrientsPollutedValues();

    setTimeout(() => {
      this.store.dispatch(new LoadAvailableTgaCrops());
    }, 0);
  }

  sortSoilAnalyses(soilAnalysis: SoilAnalysis[]) {
    return soilAnalysis.sort((a, b) => {
      const aYear = Number(a?.samplingDateTime?.substring(0, 4));
      const bYear = Number(b?.samplingDateTime?.substring(0, 4));
      if (!a?.description || !b?.description || !aYear || !bYear) {
        return 0;
      } else if (aYear !== bYear) {
        return aYear < bYear ? 1 : -1;
      } else if (a.description.toUpperCase() !== b.description.toUpperCase()) {
        return a.description.toUpperCase() < b.description.toUpperCase() ? -1 : 1;
      }
      return 0;
    });
  }

  getSoilAnalysisYear(soilAnalysis: SoilAnalysis) {
    const soilAnalysisYear = Number(soilAnalysis?.samplingDateTime?.substring(0, 4));
    return soilAnalysis && soilAnalysisYear ? `(${soilAnalysisYear})` : '';
  }

  setCropVarietiesSubscription = (cropId: number): Subscription =>
    this.cropVarietiesByCropIds$.subscribe((cropVarietiesByCropIds) => {
      const allCropVarieties =
        cropVarietiesByCropIds.find((x) => x.id === cropId)?.cropVarieties ?? [];
      this.currentCropVariety = allCropVarieties.find(
        (variety) => variety.id === this.data.cropField.cropVarietyId,
      );

      if (this.canChangeBemCropVariety()) {
        return;
      }

      this.cropVarieties = allCropVarieties;
      const varietyData = this.cropVarieties.filter(
        (variety) => variety.id === this.data.cropField.cropVarietyId,
      );
      if (this.data.cropField.cropVarietyId && !varietyData.length) {
        this.formGroup.get('cropVarietyId').setValue(null);
      } else {
        this.formGroup.get('cropVarietyId').setValue(this.data.cropField.cropVarietyId);
      }

      this.setFilteredCropVarieties();
    });

  loadProvince() {
    this.isPrefillingProvince = true;

    const provinceField = this.formGroup.controls.province;
    const outsideDutchBordersField = this.formGroup.controls.outsideDutchBorders;
    provinceField.disable();

    this.subscription.add(
      this.provinceService.getProvinceFromFeature(this.data.cropField?.feature).subscribe(
        (provinceMatch) => {
          if (provinceMatch) {
            provinceField.setValue(provinceMatch[0] as Province);
          } else {
            outsideDutchBordersField.setValue(true);
          }
          this.finalizePrefillingProvince(provinceMatch === undefined);
        },
        (_) => {
          this.finalizePrefillingProvince(false);
        },
        () => {},
      ),
    );
  }

  finalizePrefillingProvince(outsideDutchBorders: boolean) {
    const provinceField = this.formGroup.controls.province;
    this.isPrefillingProvince = false;
    if (!outsideDutchBorders) {
      provinceField.enable();
    }
  }

  loadLayerData() {
    // Fields
    const soilTypeIdField = this.formGroup.controls.soilTypeId;
    const isGroundWaterPreservationField = this.formGroup.controls.isGroundWaterPreservation;
    const isSandySoilField = this.formGroup.controls.isSandySoil;

    soilTypeIdField.disable();
    isGroundWaterPreservationField.disable();
    isSandySoilField.disable();

    // Feature
    const feature = this.data.cropField?.feature;
    if (!feature) {
      soilTypeIdField.enable();
      isGroundWaterPreservationField.enable();
      isSandySoilField.enable();
      this.store.dispatch(new PrefillCropField(false));
      return;
    }

    // Call backend validation endpoint
    const cropFieldToUpsert = transformedCropFields(
      [this.data.cropField],
      CoordinateSystem.PseudoMercator,
      CoordinateSystem.WorldGeodetic,
    )[0];
    this.cropFieldService.validateGeometry({ feature: cropFieldToUpsert.feature }).subscribe({
      next: (validation) => {
        // Fill values from backend validation
        if (validation.suggestedSoilTypeId) {
          soilTypeIdField.setValue(validation.suggestedSoilTypeId);
        }
        if (validation.isGroundWaterPreservation) {
          isGroundWaterPreservationField.setValue(validation.isGroundWaterPreservation);
        }
        if (validation.isSandySoil) {
          isSandySoilField.setValue(validation.isSandySoil);
        }
        this.isNutrientsPollutedAreaFromLayer$.next(validation.isNutrientsPollutedArea);

        // End loading + enable fields
        soilTypeIdField.enable();
        isGroundWaterPreservationField.enable();
        isSandySoilField.enable();

        this.store.dispatch(new PrefillCropField(false));
      },
      error: (error) => {
        console.error('Error validating crop field geometry:', error);
        // Enable fields even on error
        soilTypeIdField.enable();
        isGroundWaterPreservationField.enable();
        isSandySoilField.enable();
        this.store.dispatch(new PrefillCropField(false));
      },
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private filter(value: string, items: Crop[]): Crop[];
  private filter(value: string, items: CropVariety[]): CropVariety[];
  private filter(value: string, items: Crop[] | CropVariety[]) {
    return [...items]
      .sort((a, b) =>
        !(a.name && b.name) ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )
      .filter((option) =>
        !option.name ? false : option.name.toLowerCase().includes(value.toLowerCase()),
      );
  }

  private removeDuplicateCrops(items: Crop[]): Crop[] {
    return items.filter(
      (v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i,
    );
  }

  private filterSoilAnalyses(value: string, items: SoilAnalysis[]) {
    return [...items]
      .sort((a, b) =>
        !(a.description && b.description)
          ? 0
          : a.description.toLowerCase() > b.description.toLowerCase()
            ? 1
            : -1,
      )
      .filter((option) =>
        !option.description
          ? false
          : option.description.toLowerCase().includes(value.toLowerCase()),
      );
  }

  private getStartDateValidators(endDate: Date) {
    const validators = [Validators.required];
    if (endDate) {
      const minYear = endDate.getFullYear() - 100;
      validators.push(AgrDateValidators.minDate(new Date(minYear, 1, 1)));
      validators.push(AgrDateValidators.maxDate(endDate));
    }
    return validators;
  }

  private getEndDateValidators(startDate: Date) {
    const validators = [Validators.required];
    if (startDate) {
      validators.push(AgrDateValidators.minDate(startDate));
    }

    const maxYear = (startDate ? startDate.getFullYear() : new Date().getFullYear()) + 100;
    validators.push(AgrDateValidators.maxDate(new Date(maxYear, 12, 31)));
    return validators;
  }

  private getProvinceValidators(outsideDutchBorders: boolean) {
    return outsideDutchBorders ? [] : [Validators.required];
  }

  private setAreaInHectares() {
    const feature = this.data.cropField.feature;

    this.formGroup.get('areaInHectares').setValue(this.geoCalculation.getAreaInHectares(feature), {
      emitEvent: false,
    });
  }

  private setProvinceField(outsideDutchBorders: boolean): void {
    const provinceControl = this.formGroup.get('province');
    if (outsideDutchBorders) {
      provinceControl.setValue(null);
      provinceControl.disable();
    } else {
      provinceControl.enable();
    }
  }

  private setStartDate(year: number, month: number, day: number) {
    this.formGroup.get('startDate').setValue(new Date(year, month - 1, day));
  }

  private setEndDate(year: number, month: number, day: number) {
    this.formGroup.get('endDate').setValue(new Date(year, month - 1, day));
  }

  private loadCropVarieties(cropId: number) {
    if (cropId) {
      this.store.dispatch(new LoadCropVarietiesByCropIdRequest(cropId));
    }
  }

  displayCrop() {
    return (cropId?: number) =>
      cropId ? this.data.crops.find((crop) => crop.id === cropId).name : '';
  }

  displayCropVariety() {
    return (id?: number) =>
      this.cropVarieties.find((cropVariety) => cropVariety.id === id)?.name ??
      (this.currentCropVariety?.id === id ? this.currentCropVariety.name : '');
  }

  confirmEditCropField(data: EditCropField) {
    if (this.isPrefillingProvince) {
      const provinceField = this.formGroup.controls.province;
      provinceField.enable();
      provinceField.markAllAsTouched();
      return;
    }

    this.isPrefilling$.pipe(take(1)).subscribe((prefilling: boolean) => {
      if (prefilling || !this.isConfirmDataValid({ ...this.data.cropField, ...data })) {
        return;
      }
      this.confirm(this.getConfirmData(data));
    });
  }

  altEditCropField(data: EditCropField) {
    if (this.isPrefillingProvince) {
      const provinceField = this.formGroup.controls.province;
      provinceField.enable();
      provinceField.markAsTouched();
      return;
    }

    this.isPrefilling$.pipe(take(1)).subscribe((prefilling: boolean) => {
      this.formGroup.markAllAsTouched();
      if (prefilling || !this.isConfirmDataValid({ ...this.data.cropField, ...data })) {
        return;
      }
      this.alt(this.getConfirmData(data));
    });
  }

  cancelEditCropField() {
    this.store.dispatch(new PrefillCropField(false));
    this.cancel();
  }

  isConfirmDataValid(data: EditCropField) {
    return (
      this.formGroup.valid && (data.cropId || data.cropId === 0) && data.startDate && data.endDate
    );
  }

  getConfirmData(data: EditCropField) {
    const result = {
      ...this.data.cropField,
      ...data,
      startDate: new Date(
        Date.UTC(data.startDate.getFullYear(), data.startDate.getMonth(), data.startDate.getDate()),
      ).toISOString(),
      endDate: new Date(
        Date.UTC(data.endDate.getFullYear(), data.endDate.getMonth(), data.endDate.getDate()),
      ).toISOString(),
    };

    // The edited properties are overlayed on the original data.
    // If the edited province is null, the original data will be used.
    // Hence, we overwrite the province when outsideDutchBorders = true.
    if (data.outsideDutchBorders) {
      result.province = null;
    }

    return result;
  }

  setDefaultStartAndEndDate(crop: Crop) {
    const startYear = crop.isStartDateInPreviousYear ? this.data.cropYear - 1 : this.data.cropYear;
    const startMonth = crop.defaultStartDate.month;
    const startDay = crop.defaultStartDate.day;

    this.setStartDate(startYear, startMonth, startDay);

    const endYear = this.data.cropYear;
    const endMonth = crop.defaultEndDate.month;
    const endDay = crop.defaultEndDate.day;

    this.setEndDate(endYear, endMonth, endDay);
  }

  setCalculatedEndDate(crop: Crop) {
    this.cropDateService
      .calculateCropDates(crop.id, new Date(this.formGroup.get('startDate').value.toString()))
      .subscribe((data) => {
        this.formGroup.get('endDate').setValue(new Date(data));
      });
  }

  addDaysToDate(date: Date, days: number) {
    date.setDate(date.getDate() + days);

    return date;
  }

  getSoilAnalysesFormArray() {
    return this.formGroup.get('soilAnalysisIds') as FormArray;
  }

  addSoilAnalysis(event: MatChipInputEvent): void {
    const input = event.input;

    if (input) {
      input.value = '';
    }

    this.soilAnalysesControl.setValue(null);
  }

  removeSoilAnalysis(index: number): void {
    this.soilAnalysesFormArray.removeAt(index);
  }

  selectSoilAnalysis(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value === false) {
      this.store.dispatch(
        SoilAnalysisActions.editManualSoilAnalysis({
          manualSoilAnalysis: {
            id: 0,
            characteristics: {},
            samplingDateTime: new Date().toISOString(),
          } as ManualSoilAnalysis,
        }),
      );
    } else {
      const selectedSoilAnalysisId = event.option.value as string;
      this.soilAnalysesFormArray.push(new FormControl(parseInt(selectedSoilAnalysisId, 10)));
    }
    this.soilAnalysesInput.nativeElement.value = '';
    this.soilAnalysesControl.setValue(null);
  }

  getSoilTypeMatch(soilTypeFertilizerLaw: string, soilTypes: SoilType[]) {
    if (soilTypeFertilizerLaw) {
      return soilTypes.find(
        (soilType) => soilType.name.toLowerCase() === soilTypeFertilizerLaw.toLowerCase(),
      );
    }
    return undefined;
  }

  isParticipatingToBem(): boolean {
    return this.isExistingCropField() && this.data.cropField.isParticipatingToBem === true;
  }

  private isExistingCropField(): boolean {
    return this.data.cropField.id > 0;
  }

  canChangeBemCropVariety(): boolean {
    return (
      this.isParticipatingToBem() &&
      this.data.cropField.isCancellingBem !== true &&
      this.data.cropField.isPlantDateConfirmed !== true
    );
  }

  private loadAvailableBemCropVarieties(): void {
    const cropVarietyControl = this.formGroup.controls.cropVarietyId;
    this.isLoadingBemCropVarieties = true;

    this.subscription.add(
      this.cropVarietyService
        .getAvailableBemCropVarieties(this.data.cropField.id)
        .pipe(finalize(() => (this.isLoadingBemCropVarieties = false)))
        .subscribe({
          next: (cropVarieties) => {
            this.cropVarieties = cropVarieties;
            this.setFilteredCropVarieties();
            this.refreshCurrentCropVarietyDisplay(cropVarietyControl);
            if (cropVarieties.length > 0) {
              cropVarietyControl.enable();
            } else {
              cropVarietyControl.disable();
            }
          },
          error: () => cropVarietyControl.disable(),
        }),
    );
  }

  private refreshCurrentCropVarietyDisplay(cropVarietyControl: AbstractControl): void {
    const currentCropVarietyId = this.data.cropField.cropVarietyId;
    if (currentCropVarietyId) {
      cropVarietyControl.setValue(currentCropVarietyId, { emitEvent: false });
    }
  }

  private setFilteredCropVarieties(): void {
    this.filteredCropVarieties$ = this.formGroup.controls.cropVarietyId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) =>
        this.filter(value, this.cropVarieties).filter(
          (variety) => variety.id === this.data.cropField.cropVarietyId || !variety.isDeprecated,
        ),
      ),
    );
  }

  private setupUpdateNutrientsPollutedValues() {
    this.nutrientsPollutedValues$ = combineLatest([
      this.formGroup
        .get('province')
        .valueChanges.pipe(startWith(this.formGroup.get('province').value)),
      this.formGroup
        .get('isGroundWaterPreservation')
        .valueChanges.pipe(startWith(this.formGroup.get('isGroundWaterPreservation').value)),
      this.formGroup
        .get('soilTypeId')
        .valueChanges.pipe(startWith(this.formGroup.get('soilTypeId').value)),
      this.isNutrientsPollutedAreaFromLayer$,
    ]).pipe(
      skip(this.countToSkipInitialLoad),
      map(
        ([province, isGroundWaterPreservation, soilTypeId, isNutrientsPollutedAreaFromLayer]: [
          Province,
          boolean,
          number,
          boolean,
        ]) => ({
          province,
          isGroundWaterPreservation,
          soilTypeId,
          isNutrientsPollutedAreaFromLayer,
        }),
      ),
    );

    this.subscription.add(
      this.nutrientsPollutedValues$
        .pipe(withLatestFrom(this.soilTypes$))
        .subscribe(
          ([
            { province, isGroundWaterPreservation, soilTypeId, isNutrientsPollutedAreaFromLayer },
            soilTypes,
          ]) => {
            this.formGroup
              .get('isNutrientsPollutedArea')
              .setValue(
                isNutrientsPollutedAreaFromLayer ||
                  isNutrientsPollutedArea(soilTypeId, soilTypes, province),
              );
            this.formGroup
              .get('isGroundWaterPreservationWithNitrogenCuts')
              .setValue(
                isGroundWaterPreservationWithNitrogenCuts(
                  soilTypeId,
                  soilTypes,
                  isGroundWaterPreservation,
                  province,
                ),
              );
          },
        ),
    );
  }
}
